import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "isomorphic-dompurify";
import api from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { KPATRA_CSS } from "./KarmpatraListPage";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch { return ""; }
};

export default function KarmpatraIssuePage() {
  const { slug } = useParams();
  const [issue, setIssue] = useState(null);
  const [nextItem, setNextItem] = useState(null);
  const [prevItem, setPrevItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useSEO({
    title: issue
      ? `${issue.title} — Letter No. ${String(issue.issue_number).padStart(2, "0")} | Karmpatra`
      : "Karmpatra — KARM Editorial",
    description: issue?.dek || "A letter from KARM Editorial.",
    path: `/karmpatra/${slug}`,
  });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/karmpatra/${slug}`)
      .then((r) => {
        if (!alive) return;
        setIssue(r.data.issue);
        setNextItem(r.data.next);
        setPrevItem(r.data.prev);
      })
      .catch((e) => {
        if (!alive) return;
        setNotFound(e?.response?.status === 404);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [slug]);

  return (
    <div className="kpatra-scope" data-testid="karmpatra-issue">
      <style>{KPATRA_CSS}</style>

      <header className="kp-masthead">
        <p className="kp-brand-label">
          <Link to="/karmpatra" style={{ color: "inherit", textDecoration: "none" }}>
            कर्म · Karmpatra
          </Link>
        </p>
      </header>

      {loading ? (
        <p className="kp-empty">Loading…</p>
      ) : notFound || !issue ? (
        <div className="kp-empty">
          <p>Letter not found.</p>
          <p style={{ marginTop: "1rem" }}>
            <Link to="/karmpatra" className="kp-back">Return to archive →</Link>
          </p>
        </div>
      ) : (
        <>
          <div className="kp-issue-hero-num">
            Letter <strong>No.&nbsp;{String(issue.issue_number).padStart(2, "0")}</strong>
          </div>
          <h1 className="kp-issue-hero-title">{issue.title}</h1>
          {issue.dek && <p className="kp-issue-hero-dek">{issue.dek}</p>}
          <div className="kp-issue-hero-meta">
            {issue.author || "KARM Editorial"}
            {issue.published_at && <> · {fmtDate(issue.published_at)}</>}
            {issue.reading_time_min && <> · {issue.reading_time_min} min read</>}
          </div>

          <div className="kp-divider"><span>✦</span></div>

          {/* Admin-authored HTML — DOMPurify strips scripts/onhandlers before render. */}
          <article
            className="kp-issue-body"
            data-testid="karmpatra-issue-body"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(issue.html || "", {
                ADD_ATTR: ["target", "rel"],
              }),
            }}
          />

          {/* Prev / Next */}
          {(prevItem || nextItem) && (
            <nav className="kp-nav" data-testid="karmpatra-nav">
              <div>
                {prevItem && (
                  <Link to={`/karmpatra/${prevItem.slug}`} data-testid="karmpatra-prev">
                    <div className="kp-nav-label">← Previous letter</div>
                    <div className="kp-nav-title">
                      No.&nbsp;{String(prevItem.issue_number).padStart(2, "0")} — {prevItem.title}
                    </div>
                  </Link>
                )}
              </div>
              <div className="kp-nav-next">
                {nextItem && (
                  <Link to={`/karmpatra/${nextItem.slug}`} data-testid="karmpatra-next">
                    <div className="kp-nav-label">Next letter →</div>
                    <div className="kp-nav-title">
                      No.&nbsp;{String(nextItem.issue_number).padStart(2, "0")} — {nextItem.title}
                    </div>
                  </Link>
                )}
              </div>
            </nav>
          )}
        </>
      )}

      <footer className="kp-footer">
        <div className="kp-divider"><span>✦</span></div>
        <p className="kp-brand-label">Karmpatra · A KARM publication</p>
        <p className="kp-footer-line">
          <Link to="/karmpatra" className="kp-back">All letters →</Link>
        </p>
      </footer>
    </div>
  );
}
